/**
 * @file This file contains the GameState class, which represents the current
 * state of the game, including score, item queue, and game configuration.
 * @module GameState
 */

import { calculateWeightedProbabilities } from '../lib/utils.js';
export class GameState {
    /**
     * @param {object} gameConfig - The active game configuration.
     * @param {boolean} isCampaign - Indicates if the current game is a campaign level.
     * @param {number} queueSize - The desired size of the item queue.
     * @param {import('../lib/EventEmitter.js').EventEmitter} eventEmitter - The global event emitter.
     */
    constructor(gameConfig, isCampaign, queueSize, eventEmitter) {
        /**
         * The maximum number of items the collector can hold.
         * @type {number}
         */
        this.COLLECTOR_CAPACITY = 5;

        this.eventEmitter = eventEmitter;
        /**
         * The unique ID of the save slot this game state is associated with.
         * @type {string|null}
         */
        this.saveSlotId = null;
        /**
         * The active game configuration, including whether it's a campaign level.
         * @type {object}
         */
        this.activeGameConfig = {
            ...gameConfig,
            // TECH DEBT: `gridSize` here refers to the PLAYABLE area size.
            isCampaignLevel: isCampaign,
            turnsPerAnimal: gameConfig.turnsPerAnimal,
            trapScore: gameConfig.trapScore
        };

        /**
         * The current score of the game.
         * @type {number}
         */
        this.score = 0;

        /**
         * The queue of upcoming items to be placed on the grid.
         * @type {Array<object>}
         */
        this.itemQueue = [];

        /**
         * Indicates if the game is over.
         * @type {boolean}
         */
        this.isGameOver = false;

        /**
         * Indicates if the current game is part of a campaign.
         * @type {boolean}
         */
        this.isCampaignLevel = isCampaign;

        /**
         * The number of turns remaining in the current game.
         * @type {number}
         */
        this.numberOfTurnsRemaining = 0;

        /**
         * The number of turns taken in the current game.
         * @type {number}
         */
        this.numberOfTurnsTaken = 0;

        /**
         * The number of merges performed in the current game session.
         * @type {number}
         */
        this.sessionMerges = 0;
        /**
         * The number of animals crated in the current game session.
         * @type {number}
         */
        this.cratesBuilt = 0;
        /**
         * The number of power-ups used in the current game session.
         * @type {number}
         */
        this.powerupsUsed = 0;
        /**
         * The number of disasters survived in the current game session.
         * @type {number}
         */
        this.disastersSurvived = 0;

        /**
         * The column index of the player's collector in Rolling Grid mode.
         * @type {number}
         */
        this.collectorPosition = 0;

        /**
         * An array of items currently held in the collector's bucket.
         * @type {Array<object>}
         */
        this.collectorBucket = [];

        this.activeGameConfig.allItems = [];
        this._populateInitialQueue(queueSize);
    }

    /**
     * Increments the number of disasters survived in the current session.
     * @param {number} [count=1] - The number of disasters to add.
     */
    incrementDisastersSurvived(count = 1) {
        this.disastersSurvived = (this.disastersSurvived || 0) + count;
    }

    /**
     * Increments the number of power-ups used in the current session.
     * @param {number} [count=1] - The number of power-ups to add.
     */
    incrementPowerupsUsed(count = 1) {
        this.powerupsUsed = (this.powerupsUsed || 0) + count;
    }

    /**
     * Increments the number of crates built in the current session.
     * @param {number} [count=1] - The number of crates to add.
     */
    incrementCratesBuilt(count = 1) {
        this.cratesBuilt = (this.cratesBuilt || 0) + count;
    }

    /**
     * Increments the number of merges in the current session.
     * @param {number} [count=1] - The number of merges to add.
     */
    incrementSessionMerges(count = 1) {
        this.sessionMerges = (this.sessionMerges || 0) + count;
    }

    /**
     * Gets the number of merges in the current session.
     * @returns {number} The number of merges.
     */
    getSessionMerges() {
        return this.sessionMerges || 0;
    }

    /**
     * Populates the initial item queue with random items.
     * @private
     * @param {number} queueSize - The desired size of the queue.
     */
    _populateInitialQueue(queueSize) {
        // The queue is not used in rolling mode.
        if (this.activeGameConfig.gameMode === 'rolling') return;

        const size = this.activeGameConfig.isRandomQueueEnabled ? queueSize : 1;
        // Ensure the very first item is NOT a power-up
        this.itemQueue.push(this.getRandomQueueItem(false)); 
        for (let i = 1; i < size; i++) {
            this.itemQueue.push(this.getRandomQueueItem());
        }
    }

    /**
     * Adds a specified amount to the current score.
     * @param {number} amount - The amount to add to the score.
     */
    addScore(amount) {
        this.score += amount;
        this.eventEmitter.emit('achievement:scoreUpdated', { newScore: this.score, gameState: this });
    }

    /**
     * Calculates the score for a merge based on the number of items, their tier, and game rules.
     * @param {number} groupSize - The number of items that were merged.
     * @param {number} itemTier - The 0-based tier index of the items that were merged.
     * @param {boolean} [isGeneric=false] - True if the merge involves generic items like crates.
     * @returns {number} The calculated score value.
     */
    calculateMergeScore(groupSize, itemTier, isGeneric = false) {
        if (isGeneric) {
            return groupSize * (this.activeGameConfig.scoring.chestMerge || 0);
        }
        return groupSize * (this.activeGameConfig.scoring.baseMerge || 0) * (itemTier + 1);
    }

    /**
     * Returns a random item for the queue based on weighted probability.
     * Power-ups have a lower chance of appearing.
     * @param {boolean} [allowPowerUp=true] - Whether to allow power-ups to be returned.
     * @returns {object} A random item object.
     */
    getRandomQueueItem(allowPowerUp = true) {
        // 5% chance of getting a power-up, only if allowed
        if (allowPowerUp && this.activeGameConfig.isPowerupsEnabled && Math.random() < 0.05) {
            const powerUps = Object.values(this.activeGameConfig.allItems).filter(item => item && item.isPowerUp);
            if (powerUps.length > 0) {
                return powerUps[Math.floor(Math.random() * powerUps.length)];
            }
        }

        if (!this.activeGameConfig.isRandomQueueEnabled) {
            // If random queue is disabled, always return the first item of the path.
            return this.activeGameConfig.upgradePath[0];
        }

        if (this.activeGameConfig.gameMode === 'crafting') {
            // For crafting, the upgradePath is the weighted pool of items.
            // We just pick one at random.
            const placeableItems = this.activeGameConfig.upgradePath;
            if (placeableItems.length === 0) return null; // Should not happen
            return placeableItems[Math.floor(Math.random() * placeableItems.length)];
        } else if (this.activeGameConfig.isMiniMerge) {
            // For Mini-Merge, only use the first 8 items to simulate a slot machine.
            const placeableItems = this.activeGameConfig.upgradePath.slice(0, 8);
            if (placeableItems.length === 0) return null;
            return placeableItems[Math.floor(Math.random() * placeableItems.length)];
        } else {
            // For standard linear mode, use the original weighted logic.
            const placeableItems = this.activeGameConfig.upgradePath.slice(0, -1); // Exclude the final item
            if (placeableItems.length === 0) return null;
            
            const { weights, totalWeight } = calculateWeightedProbabilities(placeableItems);
            let random = Math.random() * totalWeight;
            for (let i = 0; i < placeableItems.length; i++) {
                random -= weights[i];
                if (random <= 0) {
                    return placeableItems[i];
                }
            }
            return placeableItems[0];
        }
    }

    /**
     * Checks if the win condition for the current game has been met.
     * If it's a campaign level and the target score is reached, sets game over and calls the win callback.
     * @param {function(): void} onWinCallback - The function to call if the win condition is met.
     * @returns {boolean} True if the game was won, otherwise false.
     */
    checkWinCondition(onWinCallback) {
        if (this.isCampaignLevel && this.score >= this.activeGameConfig.targetScore) {
            this.isGameOver = true;
            return true;
        }
        return false;
    }
}